import React, { memo, useEffect, useRef, useState } from "react";
import { Control, FieldError, useController } from "react-hook-form";
import { OfferSaleType } from "../../../common/dataTypes/OfferSaleType";
import Button from "../../../components/ui/Button/Button";
import ButtonWithConfirmation from "../../../components/ui/ButtonWithConfirmation/ButtonWithConfirmation";
import { Portal } from "../../../components/ui/Portal/Portal";
import ValidationLabel from "../../../components/ui/ValidationLabel/ValidationLabel";
import styles from "../OfferSale.module.scss";

import langs from "../../../common/services/languageService";
const { labels } = langs;

interface ReceiptPhotoFieldProps {
  control: Control<OfferSaleType, any>;
}

const ReceiptPhotoField: React.FC<ReceiptPhotoFieldProps> = ({ control }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const receiptInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const pendingStreamRef = useRef<MediaStream | null>(null);
  const { field } = useController({
    control,
    name: "receiptPhoto",
    rules: {
      validate: (value) => value ? true : labels.AddingReciptPhotoIsRequired(),
    },
  });
  const receiptPhoto = field.value;
  const receiptPhotoError = !receiptPhoto
    ? ({ type: "required", message: labels.AddingReciptPhotoIsRequired() } as FieldError)
    : undefined;

  useEffect(() => {
    if (!receiptPhoto) {
      setPreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(receiptPhoto);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [receiptPhoto]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const onPhotoSelected = (file: File | null) => {
    field.onChange(file);
  };

  const openNativeCameraInput = () => {
    if (receiptInputRef.current) {
      receiptInputRef.current.value = "";
      receiptInputRef.current.click();
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    pendingStreamRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsCameraReady(false);
  };

  useEffect(() => {
    if (!showCamera || !videoRef.current || !pendingStreamRef.current) {
      return;
    }

    const videoElement = videoRef.current;
    const stream = pendingStreamRef.current;

    videoElement.muted = true;
    videoElement.playsInline = true;
    videoElement.setAttribute("playsinline", "true");
    videoElement.setAttribute("webkit-playsinline", "true");
    videoElement.srcObject = stream;

    const onVideoReady = () => {
      setIsCameraReady(true);
      void videoElement.play().catch(() => {
        setIsCameraReady(false);
      });
    };

    videoElement.onloadedmetadata = onVideoReady;
    videoElement.oncanplay = onVideoReady;

    if (videoElement.readyState >= HTMLMediaElement.HAVE_METADATA) {
      onVideoReady();
    }

    pendingStreamRef.current = null;

    return () => {
      videoElement.onloadedmetadata = null;
      videoElement.oncanplay = null;
    };
  }, [showCamera]);

  const closeCamera = () => {
    stopCamera();
    setShowCamera(false);
  };

  const openCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      openNativeCameraInput();
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
        },
        audio: false,
      });

      streamRef.current = stream;
      pendingStreamRef.current = stream;
      setIsCameraReady(false);
      setShowCamera(true);
    } catch (error) {
      openNativeCameraInput();
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    // We only use the camera stream as a live preview and save a single frame as a JPG.
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          return;
        }

        const photoFile = new File([blob], `receipt-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });

        onPhotoSelected(photoFile);
        closeCamera();
      },
      "image/jpeg",
      0.92,
    );
  };

  const onPhotoRemoved = () => {
    onPhotoSelected(null);
    setShowPreview(false);
    closeCamera();

    if (receiptInputRef.current) {
      receiptInputRef.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={receiptInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className={styles.hiddenInput}
        onBlur={field.onBlur}
        onChange={(e) => onPhotoSelected(e.target.files?.[0] ?? null)}
      />
      {!receiptPhoto && (
        <Button buttonType="secondary" type="button" onClick={openCamera}>
          {labels.AddReceiptPhoto()}
        </Button>
      )}
      <ValidationLabel error={receiptPhotoError} />
      {receiptPhoto && previewUrl && (
        <div className={styles.receiptPhotoSection}>
          <button
            type="button"
            className={styles.receiptThumbnailButton}
            onClick={() => setShowPreview((current) => !current)}
            aria-label={labels.Preview()}
          >
            <img
              src={previewUrl}
              alt={labels.ReceiptPhoto()}
              className={styles.receiptThumbnail}
            />
          </button>
          <ButtonWithConfirmation
            buttonType="secondary"
            confirmationMessage={labels.DeleteReceiptPhotoConfirmation()}
            confirmLabel={labels.Yes()}
            cancelLabel={labels.No()}
            onConfirm={onPhotoRemoved}
          >
            {labels.Remove()}
          </ButtonWithConfirmation>
        </div>
      )}
      {showCamera && (
        <Portal>
          <div className={styles.receiptCameraOverlay}>
            <div className={styles.receiptCameraContent}>
              <video
                ref={videoRef}
                className={styles.receiptCameraVideo}
                autoPlay
                muted
                playsInline
              />
              <div className={styles.receiptCameraActions}>
                <Button type="button" onClick={capturePhoto} disabled={!isCameraReady}>
                  {labels.Confirm()}
                </Button>
                <Button buttonType="secondary" type="button" onClick={closeCamera}>
                  {labels.Close()}
                </Button>
              </div>
            </div>
          </div>
        </Portal>
      )}
      {showPreview && previewUrl && (
        <Portal>
          <div
            className={styles.receiptPreviewOverlay}
            onClick={() => setShowPreview(false)}
          >
            <button
              type="button"
              className={styles.receiptPreviewClose}
              onClick={() => setShowPreview(false)}
              aria-label={labels.ClosePreview()}
            >
              x
            </button>
            <img
              src={previewUrl}
              alt={labels.ReceiptPhoto()}
              className={styles.receiptPreviewImage}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </Portal>
      )}
    </>
  );
};

export default memo(ReceiptPhotoField);
